import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import NewsCard from '@/components/base/NewsCard';
import FilterBar, { type SortBy, type TimeRange } from '@/pages/category/components/FilterBar';
import { getCategoryById, getNewsByCategory } from '@/utils/news';
import type { NewsArticle } from '@/types/news';

const BASE_TIME = new Date('2026/10/12 12:00:00').getTime();
const DAY = 24 * 60 * 60 * 1000;

function withinRange(article: NewsArticle, range: TimeRange): boolean {
  const time = new Date(article.publishTime.replace(/-/g, '/')).getTime();
  if (range === 'today') return time >= BASE_TIME - DAY;
  if (range === 'week') return time >= BASE_TIME - 7 * DAY;
  if (range === 'month') return time >= BASE_TIME - 30 * DAY;
  return true;
}

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const category = getCategoryById(categoryId);

  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [sortBy, setSortBy] = useState<SortBy>('latest');
  const [activeTag, setActiveTag] = useState('');
  const [visible, setVisible] = useState(6);
  const [sheetOpen, setSheetOpen] = useState(false);

  const allArticles = useMemo(() => (category ? getNewsByCategory(category.id) : []), [category]);

  const tags = useMemo(() => {
    const set = new Set<string>();
    allArticles.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, [allArticles]);

  const filtered = useMemo(() => {
    const list = allArticles.filter((item) => {
      const okRange = withinRange(item, timeRange);
      const okTag = activeTag === '' || item.tags.includes(activeTag);
      return okRange && okTag;
    });
    return list.sort((a, b) =>
      sortBy === 'hot'
        ? b.views - a.views
        : new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime(),
    );
  }, [allArticles, timeRange, activeTag, sortBy]);

  const reset = () => {
    setTimeRange('all');
    setSortBy('latest');
    setActiveTag('');
    setVisible(6);
  };

  if (!category) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-20 text-center md:px-6">
        <Seo title="分类不存在-爆料网" description="抱歉，你访问的新闻分类不存在。" path={`/category/${categoryId}`} />
        <i className="ri-error-warning-line text-5xl text-foreground-300" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900">未找到该新闻分类</h1>
        <p className="mt-2 text-sm text-foreground-500">该分类可能已调整，请返回首页浏览其他内容。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <>
      <Seo
        title={`【${category.name}】-爆料网权威新闻爆料`}
        description={category.description}
        keywords={`${category.name},爆料网,新闻爆料,最新新闻,热点事件`}
        path={`/category/${category.id}`}
      />

      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <nav aria-label="面包屑导航" className="flex items-center gap-1 text-sm text-foreground-500">
          <Link to="/" className="inline-flex items-center gap-1 hover:text-primary-600">
            <i className="ri-home-4-line" />
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">{category.name}</span>
        </nav>

        <div className="mt-4 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:items-center md:p-6">
          <div className="h-24 w-full shrink-0 overflow-hidden rounded-md md:w-40">
            <img
              src={category.cover}
              alt={category.name}
              title={`${category.name} - 爆料网新闻爆料`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-black text-foreground-950">
              {category.name}
              <span className="ml-2 align-middle text-sm font-normal text-foreground-500">
                共 {allArticles.length} 条爆料
              </span>
            </h1>
            <p className="mt-2 text-sm leading-6 text-foreground-600">{category.intro}</p>
          </div>
        </div>

        <div className="mt-6 hidden md:block">
          <FilterBar
            timeRange={timeRange}
            sortBy={sortBy}
            tags={tags}
            activeTag={activeTag}
            onTimeRangeChange={setTimeRange}
            onSortChange={setSortBy}
            onTagChange={setActiveTag}
            onReset={reset}
          />
        </div>

        <div className="mt-6 flex items-center justify-between md:hidden">
          <p className="text-sm text-foreground-500">共 {filtered.length} 条结果</p>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700"
          >
            <i className="ri-filter-3-line" />
            筛选
          </button>
        </div>

        <div className="mt-6 flex flex-col gap-4">
          {shown.length > 0 ? (
            shown.map((article) => <NewsCard key={article.id} article={article} variant="horizontal" />)
          ) : (
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <i className="ri-search-eye-line text-5xl text-foreground-300" />
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-800">暂无相关新闻</h3>
              <p className="mt-2 text-sm text-foreground-500">当前筛选条件下没有匹配的爆料，试试调整筛选条件。</p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-refresh-line" />
                重置筛选条件
              </button>
            </div>
          )}
        </div>

        {shown.length > 0 ? (
          <div className="mt-6 flex justify-center">
            {hasMore ? (
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + 6)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-down-line" />
                加载更多
              </button>
            ) : (
              <span className="text-xs text-foreground-400">已加载全部 {filtered.length} 条新闻</span>
            )}
          </div>
        ) : null}
      </div>

      {sheetOpen ? (
        <div className="fixed inset-0 z-[60] flex items-end bg-foreground-950/50 md:hidden" onClick={() => setSheetOpen(false)}>
          <div className="max-h-[80vh] w-full overflow-y-auto rounded-t-lg bg-background-50 p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setSheetOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterBar
              timeRange={timeRange}
              sortBy={sortBy}
              tags={tags}
              activeTag={activeTag}
              onTimeRangeChange={setTimeRange}
              onSortChange={setSortBy}
              onTagChange={setActiveTag}
              onReset={reset}
            />
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="mt-4 w-full rounded-md bg-primary-600 py-2.5 text-sm font-medium text-background-50"
            >
              查看 {filtered.length} 条结果
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}